import { useMemo } from 'react';

import { useResolution } from './hooks/useResolution';

type AssetPriority = 'required' | 'background';

export interface ImageAsset {
  key: string;
  type: 'image';
  src: string;
  priority?: AssetPriority;
}

export interface JsonAsset {
  key: string;
  type: 'json';
  src: string;
  priority?: AssetPriority;
}

export interface VideoAsset {
  key: string;
  type: 'video';
  src: string;
  priority?: AssetPriority;
}

export interface FontAsset {
  key: string;
  type: 'font';
  src: string;
  priority?: AssetPriority;
}

export interface RiveAsset {
  key: string;
  type: 'rive';
  src: string;
  priority?: AssetPriority;
}

export interface CustomAnimationAsset {
  key: string;
  type: 'custom_animation';
  jsonSrc: string; // JSON file defining animations and frame paths
  priority?: AssetPriority;
  // Animations to load in the initial (blocking) pass; remaining animations load in background if loadRestInBackground is true
  eagerAnimations?: string[];
  loadRestInBackground?: boolean;
}

export type AssetConfigEntry = ImageAsset | JsonAsset | VideoAsset | RiveAsset | CustomAnimationAsset | FontAsset;

// Animation frame data for custom animations
export interface FrameSizeMetadata {
  originalSize: [number, number];
  croppedSize: [number, number];
  bbox: [number, number, number, number];
}

export interface CustomAnimationFrame {
  imageKey: string; // Key to the loaded image in LoadedGameAssets.images
  size?: FrameSizeMetadata; // Cropping metadata for aligning trimmed frames
  // Optional events for this frame (fires when frame is entered)
  events?: string[];
  // duration, width, height, anchor는 이제 CustomAnimationSequence 레벨에서 관리
}

export interface CustomAnimationSequence {
  frames: CustomAnimationFrame[];
  frameRate: number; // Frames per second for this sequence
  loop: boolean | number[]; // boolean for full loop, number[] for specific frame indices to loop
  frameWidth: number; // Common width for all frames in this sequence
  frameHeight: number; // Common height for all frames in this sequence
  anchor: { x: number; y: number }; // Common anchor for all frames
  // Optional per-frame events: map of frameIndex -> event names
  frameEvents?: Record<number, string[]>;
}

// Loaded Assets Structure
export interface LoadedGameAssets {
  videos: Record<string, string>;
  images: Record<string, HTMLImageElement>;
  jsonData: Record<string, any>;
  riveFiles: Record<string, ArrayBuffer>;
  fonts: Record<string, ArrayBuffer>;
  customAnimations: Record<string, Record<string, CustomAnimationSequence>>; // e.g., customAnimations['ufo']['idle']
}

const fontAssets: AssetConfigEntry[] = [
  // Fonts
  { key: 'bungee', type: 'font', src: '/Bungee.ttf' },
];

const riveAssets: AssetConfigEntry[] = [
  { key: 'board', type: 'rive', src: '/blinko/board.riv' },
  { key: 'play-button', type: 'rive', src: '/blinko/play-button.riv' },
];

const getResponsiveRiveAssets = (isMobile: boolean): AssetConfigEntry[] => [
  { key: 'result', type: 'rive', src: isMobile ? '/blinko/result-v.riv' : '/blinko/result-h.riv' },
  { key: 'bonus-round', type: 'rive', src: isMobile ? '/blinko/bonus-round-v.riv' : '/blinko/bonus-round-h.riv' },
  { key: 'share', type: 'rive', src: isMobile ? '/blinko/share-v.riv' : '/blinko/share-h.riv' },
];

const getBackgroundAssets = (isMobile: boolean): AssetConfigEntry[] => {
  const assets: AssetConfigEntry[] = [
    { key: 'bg-day-image', type: 'image', src: '/blinko/bg-day.jpg' },
    { key: 'bg-night-image', type: 'image', src: '/blinko/bg-night.jpg', priority: 'background' },
  ];

  if (!isMobile) {
    assets.push(
      // { key: 'bg-day-video', type: 'video', src: '/blinko/bg-day.mp4' },
      { key: 'bg-night-video', type: 'video', src: '/blinko/bg-night.mp4', priority: 'background' },
    );
  }

  return assets;
};

const boardAndAnimationAssets: AssetConfigEntry[] = [
  { key: 'pin', type: 'image', src: '/blinko/BOARD/pin.png' },
  { key: 'special-pin', type: 'image', src: '/blinko/BOARD/special-pin.png' },
  // Bins
  { key: 'hole', type: 'image', src: '/blinko/BOARD/hole.png' },
  { key: 'hole-divider', type: 'image', src: '/blinko/BOARD/hole-divider.png' },
  //
  { key: 'letters-off-b', type: 'image', src: '/blinko/LETTERS/off-b.png' },
  { key: 'letters-off-o', type: 'image', src: '/blinko/LETTERS/off-o.png' },
  { key: 'letters-off-n', type: 'image', src: '/blinko/LETTERS/off-n.png' },
  { key: 'letters-off-u', type: 'image', src: '/blinko/LETTERS/off-u.png' },
  { key: 'letters-off-s', type: 'image', src: '/blinko/LETTERS/off-s.png' },
  { key: 'letters-on-b', type: 'image', src: '/blinko/LETTERS/on-b.png' },
  { key: 'letters-on-o', type: 'image', src: '/blinko/LETTERS/on-o.png' },
  { key: 'letters-on-n', type: 'image', src: '/blinko/LETTERS/on-n.png' },
  { key: 'letters-on-u', type: 'image', src: '/blinko/LETTERS/on-u.png' },
  { key: 'letters-on-s', type: 'image', src: '/blinko/LETTERS/on-s.png' },
  {
    key: 'light-beam',
    type: 'custom_animation',
    jsonSrc: '/blinko/LIGHT-BEAM/light-beam.json',
    priority: 'background',
  },
  {
    key: 'bee-sparkling',
    type: 'custom_animation',
    jsonSrc: '/blinko/BEE-SPARKLING/bee-sparkling.json',
    priority: 'background',
  },
];

export const createGameAssetConfig = (isMobile: boolean): AssetConfigEntry[] => [
  ...fontAssets,
  ...riveAssets,
  ...getResponsiveRiveAssets(isMobile),
  ...getBackgroundAssets(isMobile),
  ...boardAndAnimationAssets,
];

export const useGameAssetConfig = () => {
  const { isMobile } = useResolution();
  return useMemo(() => createGameAssetConfig(isMobile), [isMobile]);
};
